interface Props {
  page: number;
  totalPages: number;
  onChange: (next: number) => void;
}

const ELLIPSIS = "ellipsis" as const;
type Item = number | typeof ELLIPSIS;

/** Antd-style page list: always show first + last, current ± 1, ``…`` for gaps. */
function buildItems(page: number, totalPages: number): Item[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_unused, index) => index + 1);
  }
  const items: Item[] = [1];
  const left = Math.max(2, page - 1);
  const right = Math.min(totalPages - 1, page + 1);
  if (left > 2) items.push(ELLIPSIS);
  for (let pageNum = left; pageNum <= right; pageNum++) items.push(pageNum);
  if (right < totalPages - 1) items.push(ELLIPSIS);
  items.push(totalPages);
  return items;
}

export function Pagination({ page, totalPages, onChange }: Props) {
  if (totalPages <= 1) return null;
  const items = buildItems(page, totalPages);
  return (
    <div className="pagination">
      <button
        type="button"
        disabled={page <= 1}
        onClick={() => onChange(Math.max(1, page - 1))}
        aria-label="Previous page"
      >
        ← Prev
      </button>
      {items.map((item, index) =>
        item === ELLIPSIS ? (
          <span
            key={`ellipsis-${index}`}
            className="pagination-ellipsis"
            aria-hidden="true"
          >
            …
          </span>
        ) : (
          <button
            type="button"
            key={item}
            className={item === page ? "active" : undefined}
            aria-current={item === page ? "page" : undefined}
            onClick={() => onChange(item)}
          >
            {item}
          </button>
        ),
      )}
      <button
        type="button"
        disabled={page >= totalPages}
        onClick={() => onChange(Math.min(totalPages, page + 1))}
        aria-label="Next page"
      >
        Next →
      </button>
    </div>
  );
}
